import React, { forwardRef, useRef, useCallback, useImperativeHandle } from 'react';
import type { AnimatedSvgRef, AnimatedSvgProps } from './AnimatedSvgThumbnail.types';
import { AnimatedSvgIds, AnimationDirection, AnimatedDataAttributes } from './constants';
import classnames from 'classnames';
import styles from './AnimatedSvg.scss';

export const AnimatedSvg = forwardRef<AnimatedSvgRef, AnimatedSvgProps>(({ svgContent, className, dataHook }, ref) => {
  const svgContainerRef = useRef<HTMLDivElement | null>(null);

  useImperativeHandle(ref, () => ({
    runAnimationForward,
    runAnimationBackward,
  }));

  const runAnimation = useCallback((animationDirection: AnimationDirection) => {
    if (!svgContainerRef.current) {
      return;
    }

    const animatedTag = `animateTag${animationDirection}` as keyof typeof AnimatedSvgIds;
    const animateTags = svgContainerRef.current.querySelectorAll<SVGAnimateElement>(
      `[data-animate-id=${AnimatedSvgIds[animatedTag]}]`,
    );
    animateTags.forEach((el) => el.beginElement());

    const pathTags = svgContainerRef.current.querySelectorAll(
      `path[data-animate-id=${AnimatedSvgIds.animatedTagPath}]`,
    );
    // The final shape of the animation does not always match the defined end shape exactly.
    // Therefore, it is necessary to manually switch to the end shape
    // while preserving the start shape to use it in the opposite direction.
    pathTags.forEach((path) => {
      const currentD = path.getAttribute('d');
      const animatedEndPath = path.getAttribute(AnimatedDataAttributes[animationDirection]);
      if (animatedEndPath) {
        const oppositeDirection =
          animationDirection === AnimationDirection.FORWARD ? AnimationDirection.BACKWARD : AnimationDirection.FORWARD;
        if (!path.getAttribute(AnimatedDataAttributes[oppositeDirection])) {
          path.setAttribute(AnimatedDataAttributes[oppositeDirection], currentD || '');
        }
        path.setAttribute('d', animatedEndPath);
      }
    });
  }, []);

  const runAnimationForward = useCallback(() => {
    runAnimation(AnimationDirection.FORWARD);
  }, [runAnimation]);

  const runAnimationBackward = useCallback(() => {
    runAnimation(AnimationDirection.BACKWARD);
  }, [runAnimation]);

  return (
    <div className={className} data-hook={dataHook}>
      <div
        ref={svgContainerRef}
        dangerouslySetInnerHTML={{ __html: svgContent }}
        onMouseEnter={runAnimationForward}
        onMouseLeave={runAnimationBackward}
        className={classnames(styles.animatedSvg)}
      />
    </div>
  );
});
